/**
 * Central label library (CLAUDE.md UX-quality pass) — the ONE place a raw snake_case/camelCase
 * key, DB-shaped enum value, or field name gets turned into a business-friendly label. Every
 * screen that used to hand-roll its own `KIND_LABEL`/`statusLabel(...)` should import from here
 * instead (existing per-screen maps are left in place only where they are the literal source of
 * truth for a typed union — e.g. `types/task.ts`'s `TASK_STATUS_LABEL` — and re-exported below so
 * there is still exactly one label per concept, never two drifting copies).
 *
 * Two building blocks:
 *   - {@link humanize} — a naive, domain-ignorant Title Case fallback for ANY key this file has no
 *     curated entry for. It is deliberately conservative (never fabricates business meaning it
 *     does not have) — good enough for an unanticipated DB column, not a substitute for a curated
 *     dictionary entry once a real business term exists (docs/data-dictionary.md).
 *   - `LABEL_DICTIONARIES` + {@link label} — curated overrides sourced from
 *     `docs/data-dictionary.md` / `docs/data-model.md`, keyed by a dictionary name (e.g.
 *     `'spendCategory'`). {@link label} normalizes camelCase/kebab-case to the dictionary's
 *     snake_case keys before lookup, so `label('spendCategory', 'inStateSpend')` and
 *     `label('spendCategory', 'in_state_spend')` both resolve.
 */

/** camelCase/kebab-case/PascalCase -> snake_case (the canonical dictionary-key shape). */
export function toSnakeCase(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
}

/**
 * Naive Title Case fallback for a key this library has no curated entry for — splits on
 * snake_case/kebab-case/camelCase boundaries and capitalizes each word. E.g. `qualifying_pct` ->
 * "Qualifying Pct", `principalPhotography` -> "Principal Photography". Curated dictionaries below
 * override this wherever the real business term differs (`qualifying_pct` -> "Qualifying %" via
 * {@link FIELD_LABEL}, `principal_photography` -> "Principal photography" via
 * {@link PRODUCTION_PHASE_LABEL}).
 */
export function humanize(key: string): string {
  if (!key) return ''
  const withSpaces = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  if (!withSpaces) return ''
  return withSpaces
    .split(/\s+/)
    .map((word) => (word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Curated dictionaries (docs/data-dictionary.md / docs/data-model.md)
// ---------------------------------------------------------------------------

/** `case.kind` (V?? CHECK: rnd,film). */
export const CASE_KIND_LABEL: Record<string, string> = {
  rnd: 'R&D',
  film: 'Film & Media',
}

/** `case.overall_status` — free text (data-dictionary §Case); today's shipped shape is a simple
 * open/closed umbrella (`CaseDetailScreen`'s own prior `overallStatus === 'closed'` check). */
export const CASE_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  closed: 'Closed',
}

/**
 * `program_filing.current_state` — free text, "cartridge `Lifecycle` state" (data-model §12.2/
 * table): R&D `prepare -> review -> file -> [amend] -> [audit]`; Film `reserve -> produce -> cpa ->
 * certify -> monetize -> [amend] -> [audit]`. Also covers `FilingsListScreen`'s pre-existing
 * upper-snake values (`DRAFT`/`IN_PREPARATION`/...) — {@link label} lower-cases before lookup, so
 * both casings resolve to the one entry.
 */
export const PROGRAM_FILING_STATE_LABEL: Record<string, string> = {
  prepare: 'Prepare',
  review: 'Review',
  file: 'File',
  amend: 'Amend',
  audit: 'Audit',
  reserve: 'Reserve',
  produce: 'Produce',
  cpa: 'CPA',
  certify: 'Certify',
  monetize: 'Monetize',
  draft: 'Draft',
  in_preparation: 'In preparation',
  submitted: 'Submitted',
  under_review: 'Under review',
  certified: 'Certified',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

/** `program_filing.outcome_status` (enum: in_progress,filed,amended,under_audit,closed). */
export const PROGRAM_FILING_OUTCOME_STATUS_LABEL: Record<string, string> = {
  in_progress: 'In progress',
  filed: 'Filed',
  amended: 'Amended',
  under_audit: 'Under audit',
  closed: 'Closed',
}

/** `program_filing.submission_outcome` (enum: approved,partial,denied,withdrawn, ADR-0032). */
export const SUBMISSION_OUTCOME_LABEL: Record<string, string> = {
  approved: 'Approved',
  partial: 'Partially approved',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
}

/** `qualified_spend_line.category` (data-dictionary: in_state_spend,resident_labour,
 * nonresident_labour_capped,vendor_spend). */
export const SPEND_CATEGORY_LABEL: Record<string, string> = {
  in_state_spend: 'In-state spend',
  resident_labour: 'Resident labour',
  nonresident_labour_capped: 'Non-resident labour (capped)',
  vendor_spend: 'Vendor spend',
}

/** `production_phase.phase` (data-model: development,preproduction,principal_photography,post). */
export const PRODUCTION_PHASE_LABEL: Record<string, string> = {
  development: 'Development',
  preproduction: 'Pre-production',
  principal_photography: 'Principal photography',
  post: 'Post-production',
}

/** `production.type` — free text (config-not-code); common illustrative values only, humanize
 * fallback covers anything a tenant's cartridge introduces beyond this set. */
export const PRODUCTION_TYPE_LABEL: Record<string, string> = {
  feature_film: 'Feature film',
  tv_series: 'TV series',
  tv_movie: 'TV movie',
  commercial: 'Commercial',
  documentary: 'Documentary',
  digital_series: 'Digital series',
  animation: 'Animation',
  short_film: 'Short film',
}

/** `evidence_item.type` — free text, config-not-code (data-dictionary: "timesheet, contract, …");
 * common illustrative values only. */
export const EVIDENCE_TYPE_LABEL: Record<string, string> = {
  cpa_aup_report: 'CPA AUP report',
  timesheet: 'Timesheet',
  contract: 'Contract',
  invoice: 'Invoice',
  receipt: 'Receipt',
  payroll_register: 'Payroll register',
  chain_of_title: 'Chain-of-title document',
  loan_out_agreement: 'Loan-out agreement',
  residency_certificate: 'Residency certificate',
  w2: 'W-2',
  '1099': '1099',
}

/** `external_attestation.type` (CHECK: cpa_aup only, today). */
export const ATTESTATION_TYPE_LABEL: Record<string, string> = {
  cpa_aup: 'CPA AUP',
}

/** `evidence_item.status` (V29 CHECK: draft,accepted,quarantined — evidence-vault story, FR-M14.2/3). */
export const EVIDENCE_ITEM_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  accepted: 'Accepted',
  quarantined: 'Quarantined',
}

/** `evidence_file.av_scan_status` (V29 CHECK: pending,clean,infected — evidence-vault story, FR-M14.2). */
export const AV_SCAN_STATUS_LABEL: Record<string, string> = {
  pending: 'Scan pending',
  clean: 'Clean',
  infected: 'Infected',
}

/** `evidence_requirement.criticality` (V29 CHECK: critical,recommended — evidence-vault story, FR-M14.3/5). */
export const EVIDENCE_REQUIREMENT_CRITICALITY_LABEL: Record<string, string> = {
  critical: 'Critical',
  recommended: 'Recommended',
}

/** `evidence_requirement.status` (V29 CHECK: not_started,partial,satisfied,waived,na — evidence-vault story, FR-M14.3/5). */
export const EVIDENCE_REQUIREMENT_STATUS_LABEL: Record<string, string> = {
  not_started: 'Missing',
  partial: 'Partially satisfied',
  satisfied: 'Satisfied',
  waived: 'Waived',
  na: 'Not applicable',
}

/** `external_attestation.status` (enum: draft,uploaded,signed,rejected,withdrawn). */
export const ATTESTATION_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  uploaded: 'Uploaded',
  signed: 'Signed',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

/** `AuthorityInteraction.kind` (data-dictionary Cluster E). */
export const AUTHORITY_INTERACTION_KIND_LABEL: Record<string, string> = {
  application: 'Application',
  reservation: 'Reservation',
  certification: 'Certification',
  return_attachment: 'Return attachment',
  registration: 'Registration',
}

/** `AuthorityInteraction.status` (service-layer lifecycle-lock). */
export const AUTHORITY_INTERACTION_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  approved: 'Approved',
  denied: 'Denied',
  deferred: 'Deferred',
  withdrawn: 'Withdrawn',
}

/** `bulk_operation.scope` (enum: selection,filter). */
export const BULK_SCOPE_LABEL: Record<string, string> = {
  selection: 'By row ID',
  filter: 'By filter',
}

/**
 * `target_entity`/`entity_type` — the physical table/entity keys the Bulk-ops and ingestion
 * screens surface verbatim from the backend contract. Curated for the entities this build
 * actually targets; humanize fallback for anything else.
 */
export const ENTITY_LABEL: Record<string, string> = {
  qualified_spend_line: 'Qualified spend line',
  production: 'Production',
  production_phase: 'Production phase',
  personnel: 'Personnel',
  vendor: 'Vendor',
  loan_out: 'Loan-out company',
  chain_of_title: 'Chain-of-title record',
  case: 'Case',
  program_filing: 'Program filing',
  account: 'Account',
  legal_entity: 'Legal entity',
  evidence_item: 'Evidence item',
  residency_certification: 'Residency certification',
  contact: 'Contact',
  branch: 'Branch',
  task: 'Task',
}

/** `ingestion_pipeline.load_mode` — free text (config-not-code); common values only. */
export const LOAD_MODE_LABEL: Record<string, string> = {
  full: 'Full load',
  incremental: 'Incremental',
  delta: 'Delta',
}

/** `notification.category`/`notification_preference.category` — free text (data-dictionary:
 * "Notification category (push/SMS default to time-critical)"); common illustrative values only. */
export const NOTIFICATION_CATEGORY_LABEL: Record<string, string> = {
  task_assigned: 'Task assigned',
  time_critical: 'Time-critical',
  filing_status_change: 'Filing status change',
  authority_response: 'Authority response',
  dossier_ready: 'Dossier ready',
  bulk_operation: 'Bulk operation',
  adjustment: 'Adjustment',
}

/**
 * `program.jurisdiction`/`legal_entity.jurisdiction` display formatting — presentation-only (never
 * a business-logic branch; CLAUDE.md §2's "config, not code" governs jurisdiction RULES, not this
 * label lookup). Curated for the jurisdictions this build's cartridges/demo data actually use;
 * anything else falls back to the raw code (still meaningful — an ISO-ish jurisdiction code is not
 * a DB key) rather than a mangled `humanize()` guess.
 */
export const JURISDICTION_LABEL: Record<string, string> = {
  'US-GA': 'Georgia (US-GA)',
  'US-NY': 'New York (US-NY)',
  'US-LA': 'Louisiana (US-LA)',
  'US-CA': 'California (US-CA)',
  'US-TX': 'Texas (US-TX)',
  'US-NM': 'New Mexico (US-NM)',
  US: 'US Federal',
  CA: 'Canada',
  GB: 'United Kingdom',
  IE: 'Ireland',
  AU: 'Australia',
  AE: 'United Arab Emirates',
}

/**
 * Raw field/column names rendered directly (bulk-ops field picker, adjustment ledger, JSON blobs).
 * Keyed by the DB/logical field name (snake_case) — {@link fieldLabel} normalizes camelCase first.
 */
export const FIELD_LABEL: Record<string, string> = {
  qualifying_pct: 'Qualifying %',
  qre_pct: 'QRE %',
  in_state: 'In-state',
  resident: 'Resident',
  locked: 'Locked',
  category: 'Category',
  amount: 'Amount',
  currency: 'Currency',
  vendor_id: 'Vendor',
  personnel_id: 'Personnel',
  phase_ref: 'Phase',
  evidence_ref: 'Evidence reference',
  person_ref: 'Person reference',
  retention_policy: 'Retention policy',
  contemporaneous: 'Contemporaneous',
  hash: 'Content hash',
  region: 'Region',
  qualified: 'Qualified',
  verified: 'Verified',
  status: 'Status',
  reason: 'Reason',
}

const LABEL_DICTIONARIES = {
  caseKind: CASE_KIND_LABEL,
  caseStatus: CASE_STATUS_LABEL,
  programFilingState: PROGRAM_FILING_STATE_LABEL,
  programFilingOutcomeStatus: PROGRAM_FILING_OUTCOME_STATUS_LABEL,
  submissionOutcome: SUBMISSION_OUTCOME_LABEL,
  spendCategory: SPEND_CATEGORY_LABEL,
  productionPhase: PRODUCTION_PHASE_LABEL,
  productionType: PRODUCTION_TYPE_LABEL,
  evidenceType: EVIDENCE_TYPE_LABEL,
  evidenceItemStatus: EVIDENCE_ITEM_STATUS_LABEL,
  avScanStatus: AV_SCAN_STATUS_LABEL,
  evidenceRequirementCriticality: EVIDENCE_REQUIREMENT_CRITICALITY_LABEL,
  evidenceRequirementStatus: EVIDENCE_REQUIREMENT_STATUS_LABEL,
  attestationType: ATTESTATION_TYPE_LABEL,
  attestationStatus: ATTESTATION_STATUS_LABEL,
  authorityInteractionKind: AUTHORITY_INTERACTION_KIND_LABEL,
  authorityInteractionStatus: AUTHORITY_INTERACTION_STATUS_LABEL,
  bulkScope: BULK_SCOPE_LABEL,
  entity: ENTITY_LABEL,
  loadMode: LOAD_MODE_LABEL,
  notificationCategory: NOTIFICATION_CATEGORY_LABEL,
  jurisdiction: JURISDICTION_LABEL,
} as const

export type LabelDictionaryName = keyof typeof LABEL_DICTIONARIES

/**
 * `label('spendCategory', 'in_state_spend')` -> `"In-state spend"`. Normalizes camelCase/
 * kebab-case to the dictionary's snake_case keys first, so a raw wire value in either casing
 * resolves the same curated entry; falls back to {@link humanize} (never throws, never renders a
 * blank cell) when the dictionary has no entry — except `'jurisdiction'`, which falls back to the
 * raw code (see {@link JURISDICTION_LABEL}'s own doc comment).
 */
export function label(dictionary: LabelDictionaryName, key: string | null | undefined): string {
  if (!key) return '—'
  const dict = LABEL_DICTIONARIES[dictionary]
  const snake = toSnakeCase(key)
  const found = dict[snake] ?? dict[key]
  if (found) return found
  if (dictionary === 'jurisdiction') return key
  return humanize(key)
}

/** A raw field/column name (e.g. from a bulk-op's field picker, or a JSON diff blob's keys). */
export function fieldLabel(key: string | null | undefined): string {
  if (!key) return '—'
  const snake = toSnakeCase(key)
  return FIELD_LABEL[snake] ?? humanize(key)
}

/**
 * A `Record<string, unknown>` (e.g. `BulkOperationItemView.before`/`.after`) rendered as a
 * human-readable `"Field label: value"` list — never the raw `JSON.stringify` of DB-shaped keys.
 * `null`/empty renders the caller's own empty-state text, not this helper's concern.
 */
export function formatFieldValueRecord(record: Record<string, unknown> | null | undefined): string {
  if (!record) return '—'
  const entries = Object.entries(record)
  if (entries.length === 0) return '—'
  return entries.map(([k, v]) => `${fieldLabel(k)}: ${formatValue(v)}`).join(', ')
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}
